import {Box, Button, Stack, Typography} from '@mui/material';
import React, {Fragment, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useSetRecoilState} from 'recoil';

import {
	AppointmentBookingEnum,
	BookingScheduledEnum,
	MotorV2Enums,
	OdontoBookingTaggingEnum,
} from 'main/analytics/gaTagsEnum';
import {AlertModal, CTAButtonFixed, DataInfo} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {
	AuthorizationsEnum,
	ChatRoomsEnum,
	FacialBiometricEnum,
	FeaturesEnum,
	HomeEnum,
	OperationStateTypesEnum,
} from 'presentation/constants';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useCallChatRoom, useMediaQueries} from 'presentation/pages/private/hooks';
import {refresherState} from 'presentation/pages/states';
import {translations} from 'presentation/translations-new';
import {BookingTypeEnum, CONFIRMATION_PATH, FlowEnum} from '../../constants';
import {useBooking} from '../../hooks';
import {useRebooking} from '../../hooks/useRebooking/useRebooking';
import {ConfirmBookingButtonProps} from './types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

const {pleaseTryAgain, goBack, agreementText} = translations['pt-br'].commons;
const {
	confirmBooking,
	confirmationBooking,
	errorBookingTitle,
	errorExamBookingTitle,
	successExamBookingHeaderTitle,
	successExamBookingDescription,
	successBookingDescription,
	successBookingHeaderTitle,
	successTeleBookingHeaderTitle,
	successBookingAlert,
	successExamBookingAlert,
	bookingError,
	unavailableSchedule,
	unexpectedError,
	unauthorizedError,
	openScheduling,
} = translations['pt-br'].privateFlow.bookingFlow;
const {appointmentProtocol} =
	translations['pt-br'].privateFlow.myAppointments.myAppointmentDetails.appointmentInstructions;

export function ConfirmBookingButton({
	flowType,
	handleBack,
	variant = 'default',
	fullWidth = true,
	...stackProps
}: ConfirmBookingButtonProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);

	const {isRebooking, bookingNumber} = bookingData;
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const {isMobile} = useMediaQueries();
	const navigate = useNavigate();
	const {state} = useLocation();
	const {isEnabled: isHideConfirmationBookingModalEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_HIDE_CONFIRMATION_BOOKING_MODAL,
	);

	const {
		bookHealth,
		bookOdonto,
		bookExam,
		bookingState,
		setBookingState,
		typeError,
		setTypeError,
	} = useBooking({
		flowType,
	});

	const proceedWithBooking = async () => {
		setBookingState(OperationStateTypesEnum.NONE);
		await bookHealth(true);
	};
	const {rebookHealth, rebookOdonto, rebookExam, rebookingState, setRebookingState} =
		useRebooking({flowType});

	const setRefreshDatesAndTimes = useSetRecoilState(refresherState);

	const {callChatRoom, IS_LOADING: callChatRoomIsLoading} = useCallChatRoom();

	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isHealth = flowType === FlowEnum.HEALTH;
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isExam = flowType === FlowEnum.EXAM;

	const isTeleOrHealth = isHealth || isTelehealth;

	const bookingAndRebookingStates = [bookingState, rebookingState];
	const isError = bookingAndRebookingStates.includes(OperationStateTypesEnum.ERROR);
	const isUnauthorized = bookingAndRebookingStates.includes(OperationStateTypesEnum.UNAUTHORIZED);
	const isDone = bookingAndRebookingStates.includes(OperationStateTypesEnum.DONE);
	const isSubmitting = bookingAndRebookingStates.includes(OperationStateTypesEnum.SUBMITTING);
	const isUnavailable = bookingAndRebookingStates.includes(OperationStateTypesEnum.UNAVAILABLE);
	const isOpenScheduling = bookingAndRebookingStates.includes(
		OperationStateTypesEnum.OPEN_SCHEDULING,
	);

	const isTeleOrHealthError = isTeleOrHealth && isError;
	const isBookingOrReBookingError =
		isTeleOrHealthError && typeError !== null && ['001', '004'].includes(typeError);
	const isHourError =
		isTeleOrHealthError && typeError !== null && ['002', '003'].includes(typeError);
	const isUnexpectedError = isTeleOrHealthError && !typeError;

	const dataTagIdSuccess = isRebooking
		? AppointmentBookingEnum.REALIZOU_REMARCACAO_CONSULTA
		: AppointmentBookingEnum.REALIZOU_AGENDAMENTO_COM_SUCESSO;

	const errorBookingDialogTitle = isExam ? errorExamBookingTitle : errorBookingTitle;
	const successBookingTeleDialogHeader = isTelehealth
		? successTeleBookingHeaderTitle
		: successBookingHeaderTitle;
	const successBookingDialogHeaderTitle = isExam
		? successExamBookingHeaderTitle
		: successBookingTeleDialogHeader;
	const successBookingDialogDescription = isExam
		? successExamBookingDescription
		: successBookingDescription;
	const successBookingDialogTitle = isExam ? successExamBookingAlert : successBookingAlert;

	const {LOCATION_STEP, SPECIALITY_OR_EXAM_STEP} = getStepPosition({
		flowType,
		isManualLocation: bookingData.isManualLocation,
	});

	async function handleBooking() {
		if (isOdonto) {
			await bookOdonto();
			return;
		}

		if (isExam) {
			await bookExam();

			return;
		}
		await bookHealth();
	}

	async function handleRebooking() {
		if (bookingNumber === undefined) {
			return;
		}
		if (isOdonto) {
			await rebookOdonto(bookingNumber);
			return;
		}
		if (isExam) {
			await rebookExam(bookingNumber);
			return;
		}
		await rebookHealth(bookingNumber);
	}

	function handleCloseErrorDialogState() {
		setTypeError(null);
		if (isRebooking) {
			setRebookingState(null);
		} else {
			setBookingState(null);
		}
	}

	function handleCloseSuccessDialogState() {
		setTypeError(null);

		if (isRebooking) setRebookingState(null);

		if (!bookingNumber) {
			setBookingState(null);
			return;
		}

		const path = CONFIRMATION_PATH[bookingData.bookingType as BookingTypeEnum].replace(
			':id',
			bookingNumber,
		);

		navigate(path);
		setBookingState(null);
	}

	function handleCloseAndRedirectToInitialFlow(shouldRedirect?: boolean) {
		setTypeError(null);
		if (isRebooking) {
			setRebookingState(null);
		}
		setBookingState(null);

		if (shouldRedirect) {
			setBookingData(prev => ({
				...prev,
				unitCode: '',
				unitName: '',
				unitAddress: '',
				professionalCode: '',
				professionalName: '',
				dateAt: '',
				hourAt: '',
				specialtyCode: '',
				specialtyName: '',
				subSpecialtyCode: '',
				districtName: '',
				allDistrictsSelected: false,
				citiesNames: '',
				coordinates: '',
				isManualLocation: true,
				hasGeoDistrictsFilter: false,
			}));

			setActiveBookingFlowStep(isTelehealth ? SPECIALITY_OR_EXAM_STEP : LOCATION_STEP);
		}
	}

	function handleCloseAndRedirectToSchedule() {
		setTypeError(null);
		if (isRebooking) {
			setRebookingState(null);
		}
		setBookingState(null);

		setRefreshDatesAndTimes(state => state + 1);
		if (handleBack) {
			handleBack();
		}
	}

	function handleCloseAndRedirectToHome(shouldRedirectToHome?: boolean) {
		setTypeError(null);
		if (isRebooking) {
			setRebookingState(null);
		}
		setBookingState(null);

		if (shouldRedirectToHome) {
			navigate(HomeEnum.HOME_PAGE);
		}
	}

	async function handleCallChatRoom() {
		let type = ChatRoomsEnum.APP;

		if (isOdonto) {
			type = ChatRoomsEnum.ODONTO;
		}

		if (isExam) {
			type = ChatRoomsEnum.EXAMS;
		}
		await callChatRoom(type);
	}

	const isExamConfirmation = isExam
		? BookingScheduledEnum.CLICK_CONFIRMAR_AGENDAMENTO_EXAME
		: BookingScheduledEnum.CLICK_CONFIRMAR_AGENDAMENTO;
	const handleBookingAction = isRebooking ? handleRebooking : handleBooking;
	const isGenericError = isError && !isTeleOrHealthError;
	const hasHourOrAvailabilityError = isHourError || isUnavailable;

	useEffect(() => {
		if (state?.from === FacialBiometricEnum.FACIAL_BIOMETRIC && isTelehealth) {
			handleBookingAction();
		}
	}, [state]);

	return (
		<Fragment>
			{variant === 'fixed' ? (
				<CTAButtonFixed.Root>
					<Stack p={isMobile ? 0 : 2} pb={isMobile ? 0 : 1} gap={1} {...stackProps}>
						<CTAButtonFixed.Action
							fullWidth
							endIcon={isSubmitting ? <Loading color="inherit" size={16} /> : null}
							disabled={isSubmitting}
							size="large"
							onClick={handleBookingAction}
							data-tag-id={isExamConfirmation}>
							{confirmBooking}
						</CTAButtonFixed.Action>
						<CTAButtonFixed.Action
							data-tag-id={
								OdontoBookingTaggingEnum.CLICK_VOLTAR_STEP_CONFIRMAR_AGENDAMENTO
							}
							variant="outlined"
							size="large"
							onClick={handleBack}
							fullWidth>
							{goBack}
						</CTAButtonFixed.Action>
					</Stack>
				</CTAButtonFixed.Root>
			) : (
				<Stack gap={1} {...stackProps}>
					<Button
						data-tag-id={
							OdontoBookingTaggingEnum.CLICK_VOLTAR_STEP_CONFIRMAR_AGENDAMENTO
						}
						size="large"
						variant="outlined"
						onClick={handleBack}
						fullWidth={fullWidth}>
						{goBack}
					</Button>
					<Button
						fullWidth={fullWidth}
						size="large"
						endIcon={isSubmitting ? <Loading color="inherit" size={16} /> : null}
						disabled={isSubmitting}
						onClick={handleBookingAction}
						data-tag-id={isExamConfirmation}>
						{confirmBooking}
					</Button>
				</Stack>
			)}

			<AlertModal
				slotProps={{
					paper: {
						style: {
							maxWidth: 420,
							margin: isMobile ? 16 : 'auto',
							flex: isMobile ? 1 : 'initial',
						},
					},
				}}
				description={pleaseTryAgain}
				headerTitle={confirmationBooking}
				title={errorBookingDialogTitle}
				onClose={handleCloseErrorDialogState}
				open={isGenericError}
				variant="info"
			/>

			<AlertModal
				slotProps={{
					paper: {
						style: {
							maxWidth: 420,
							margin: isMobile ? 16 : 'auto',
							flex: isMobile ? 1 : 'initial',
						},
					},
				}}
				description={bookingError.description}
				headerTitle={bookingError.title}
				buttonLabel={bookingError.button}
				onClose={() => handleCloseAndRedirectToInitialFlow(false)}
				open={isBookingOrReBookingError}
				variant="info"
				data-tag-id={MotorV2Enums.V2_ERRO_AGENDAMENTO}
				footerContent={
					<Button
						onClick={() => handleCloseAndRedirectToInitialFlow(true)}
						variant="contained"
						size="large"
						fullWidth
						data-tag-id={MotorV2Enums.V2_CLICK_CTA_ERRO_AGENDAMENTO}>
						{bookingError.button}
					</Button>
				}
			/>

			<AlertModal
				slotProps={{
					paper: {
						style: {
							maxWidth: 420,
							margin: isMobile ? 16 : 'auto',
							flex: isMobile ? 1 : 'initial',
						},
					},
				}}
				description={unavailableSchedule.description}
				headerTitle={unavailableSchedule.title}
				buttonLabel={unavailableSchedule.button}
				onClose={handleCloseAndRedirectToSchedule}
				open={hasHourOrAvailabilityError}
				variant="info"
				data-tag-id={MotorV2Enums.V2_ERRO_HORARIO}
				buttonDataTagId={MotorV2Enums.V2_CLICK_CTA_ERRO_HORARIO}
			/>

			<AlertModal
				slotProps={{
					paper: {
						style: {
							maxWidth: 420,
							margin: isMobile ? 16 : 'auto',
							flex: isMobile ? 1 : 'initial',
						},
					},
				}}
				description={unexpectedError.description}
				headerTitle={unexpectedError.title}
				buttonLabel={unexpectedError.button}
				onClose={() => handleCloseAndRedirectToHome(false)}
				open={isUnexpectedError}
				variant="info"
				data-tag-id={MotorV2Enums.V2_MODAL_ERRO_GENERICO}
				footerContent={
					<Button
						onClick={() => handleCloseAndRedirectToHome(true)}
						variant="contained"
						size="large"
						fullWidth
						data-tag-id={MotorV2Enums.V2_CLICK_CTA_ERRO_GENERICO}>
						{unexpectedError.button}
					</Button>
				}
			/>

			<AlertModal
				slotProps={{
					paper: {
						style: {
							maxWidth: 420,
							margin: isMobile ? 16 : 'auto',
							flex: isMobile ? 1 : 'initial',
						},
					},
				}}
				description={
					<Stack gap={2}>
						<Typography variant="body1" textAlign="center">
							{unauthorizedError.description1}
						</Typography>
						<Typography variant="body1" textAlign="center">
							{unauthorizedError.description2}
						</Typography>
					</Stack>
				}
				headerTitle={unauthorizedError.title}
				buttonLabel={unauthorizedError.button1}
				onClose={handleCloseErrorDialogState}
				open={isUnauthorized}
				variant="info"
				data-tag-id={MotorV2Enums.V2_MODAL_ERRO_GENERICO}
				footerContent={
					<Stack gap={1} width="100%">
						<Button
							onClick={() => navigate(AuthorizationsEnum.AUTHORIZATIONS)}
							variant="contained"
							size="large"
							fullWidth
							data-tag-id={MotorV2Enums.V2_CLICK_CTA_ERRO_GENERICO}>
							{unauthorizedError.button1}
						</Button>
						<Button
							onClick={handleCallChatRoom}
							variant="outlined"
							size="large"
							fullWidth
							data-tag-id={MotorV2Enums.V2_CLICK_CTA_ERRO_GENERICO}
							endIcon={
								callChatRoomIsLoading ? <Loading size={16} color="inherit" /> : null
							}
							disabled={callChatRoomIsLoading}>
							{unauthorizedError.button2}
						</Button>
					</Stack>
				}
			/>

			<AlertModal
				slotProps={{
					paper: {
						style: {
							maxWidth: 420,
							margin: isMobile ? 16 : 'auto',
							flex: isMobile ? 1 : 'initial',
						},
					},
				}}
				description={
					<Stack gap={2}>
						<Typography variant="body1" textAlign="center">
							{openScheduling.description2}
						</Typography>
						<Typography variant="body1" textAlign="center">
							{openScheduling.description3}
						</Typography>
					</Stack>
				}
				title={openScheduling.description1}
				headerTitle={openScheduling.title}
				buttonLabel={openScheduling.button1}
				open={isOpenScheduling}
				onClose={() => setBookingState(OperationStateTypesEnum.NONE)}
				variant="info"
				footerContent={
					<Stack gap={1} width="100%">
						<Button
							onClick={proceedWithBooking}
							variant="contained"
							size="large"
							fullWidth>
							{openScheduling.button1}
						</Button>
						<Button
							onClick={() => setBookingState(OperationStateTypesEnum.NONE)}
							variant="outlined"
							size="large"
							fullWidth>
							{openScheduling.button2}
						</Button>
					</Stack>
				}
			/>

			{isHideConfirmationBookingModalEnabled ? null : (
				<AlertModal
					slotProps={{
						paper: {
							style: {
								maxWidth: 420,
								margin: isMobile ? 16 : 'auto',
								flex: isMobile ? 1 : 'initial',
							},
						},
					}}
					subDescription={
						<Box alignSelf="center">
							<DataInfo
								primaryText={appointmentProtocol}
								secondaryText={bookingData.protocolNumber}
								iconName="assignment-filled"
							/>
						</Box>
					}
					headerDataTagId={dataTagIdSuccess}
					buttonDataTagId={
						OdontoBookingTaggingEnum.CLICK_ENTENDI_MODAL_CONFIRMACAO_AGENDAMENTO
					}
					description={successBookingDialogDescription}
					title={successBookingDialogTitle}
					headerTitle={successBookingDialogHeaderTitle}
					open={isDone}
					onClose={handleCloseSuccessDialogState}
					variant="success"
					buttonLabel={agreementText}
				/>
			)}
		</Fragment>
	);
}
